"use client";

import { useState, useEffect, useMemo } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";
import { comparisonService, ComparisonRecord } from "@/lib/services/comparisonService";

type Product = {
  id: string;
  category: string;
  name: string;
  nameZh: string;
  brand: string;
  specs: string;
  image: string;
  alt: string;
  badge: string;
  badgeColor: string;
  moq: string;
  priceRange: [number, number];
  stockStatus: "in-stock" | "low-stock" | "out-of-stock";
};

const ALL_PRODUCTS: Product[] = [
// ── CPU ──────────────────────────────────────────────────────────────
{ id: "cpu-1", category: "cpu", name: "Intel Core i9-14900K", nameZh: "英特爾 第14代旗艦處理器", brand: "Intel", specs: "24 Cores / 5.8GHz / LGA1700 / 125W TDP", image: "https://images.unsplash.com/photo-1676315487591-7a1d5c02ecae", alt: "Intel Core i9-14900K processor chip on white background", badge: "Hot", badgeColor: "bg-orange text-white", moq: "MOQ: 10 units", priceRange: [550, 650], stockStatus: "in-stock" },
{ id: "cpu-2", category: "cpu", name: "Intel Core i9-13900KS", nameZh: "英特爾 第13代旗艦處理器", brand: "Intel", specs: "24 Cores / 6.0GHz Boost / LGA1700 / 150W TDP", image: "https://images.unsplash.com/photo-1729357671906-875e1bc2f0bf", alt: "Intel Core i9 13th generation processor on motherboard socket", badge: "6GHz", badgeColor: "bg-orange text-white", moq: "MOQ: 10 units", priceRange: [500, 600], stockStatus: "in-stock" },
{ id: "cpu-3", category: "cpu", name: "Intel Core i7-14700K", nameZh: "英特爾 第14代高階處理器", brand: "Intel", specs: "20 Cores / 5.6GHz / LGA1700 / 125W TDP", image: "https://images.unsplash.com/photo-1555616635-96c8fef588c1", alt: "Intel Core i7 14th gen processor close-up view", badge: "New", badgeColor: "bg-yellow-brand text-navy", moq: "MOQ: 10 units", priceRange: [380, 430], stockStatus: "in-stock" },
{ id: "cpu-4", category: "cpu", name: "Intel Core i5-14600K", nameZh: "英特爾 第14代主流處理器", brand: "Intel", specs: "14 Cores / 5.3GHz / LGA1700 / 125W TDP", image: "https://images.unsplash.com/photo-1694466464544-4f93703b0503", alt: "Intel Core i5 14600K processor on circuit board", badge: "Best Seller", badgeColor: "bg-navy text-white", moq: "MOQ: 20 units", priceRange: [280, 320], stockStatus: "in-stock" },
{ id: "cpu-5", category: "cpu", name: "AMD Ryzen 9 9950X", nameZh: "AMD Zen 5 旗艦處理器", brand: "AMD", specs: "16 Cores / 5.7GHz / AM5 / Zen 5 Architecture", image: "https://images.unsplash.com/photo-1613483187550-1458bbdb0996", alt: "AMD Ryzen 9 9950X Zen 5 processor on white surface", badge: "2025", badgeColor: "bg-orange text-white", moq: "MOQ: 10 units", priceRange: [600, 700], stockStatus: "in-stock" },
{ id: "cpu-6", category: "cpu", name: "AMD Ryzen 9 7950X", nameZh: "AMD 旗艦桌上型處理器", brand: "AMD", specs: "16 Cores / 5.7GHz / AM5 Socket / 170W TDP", image: "https://img.rocket.new/generatedImages/rocket_gen_img_1eb7e7d9a-1772174256659.png", alt: "AMD Ryzen 9 7950X processor hardware component", badge: "Popular", badgeColor: "bg-navy text-white", moq: "MOQ: 10 units", priceRange: [500, 600], stockStatus: "in-stock" },
{ id: "cpu-7", category: "cpu", name: "AMD Ryzen 7 7700X", nameZh: "AMD 高效能桌上型處理器", brand: "AMD", specs: "8 Cores / 5.4GHz / AM5 / 105W TDP", image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ef96da7c-1772174254938.png", alt: "AMD Ryzen 7 7700X processor chip close-up", badge: "Value", badgeColor: "bg-green-600 text-white", moq: "MOQ: 20 units", priceRange: [280, 340], stockStatus: "in-stock" },
{ id: "cpu-8", category: "cpu", name: "AMD Ryzen 5 7600X", nameZh: "AMD 主流桌上型處理器", brand: "AMD", specs: "6 Cores / 5.3GHz / AM5 / 105W TDP", image: "https://img.rocket.new/generatedImages/rocket_gen_img_15b4c89a0-1772174256385.png", alt: "AMD Ryzen 5 7600X processor for mainstream builds", badge: "Budget", badgeColor: "bg-green-600 text-white", moq: "MOQ: 20 units", priceRange: [200, 250], stockStatus: "in-stock" },
// ── GPU ──────────────────────────────────────────────────────────────
{ id: "gpu-1", category: "gpu", name: "NVIDIA RTX 4090", nameZh: "NVIDIA 旗艦顯示卡", brand: "NVIDIA", specs: "16384 CUDA / 24GB GDDR6X / 450W TDP / PCIe 4.0", image: "https://img.rocket.new/generatedImages/rocket_gen_img_184602c4d-1772079187447.png", alt: "NVIDIA RTX 4090 flagship graphics card with triple fan cooler", badge: "Flagship", badgeColor: "bg-orange text-white", moq: "MOQ: 5 units", priceRange: [1600, 2000], stockStatus: "low-stock" },
{ id: "gpu-2", category: "gpu", name: "NVIDIA RTX 4080 Super", nameZh: "NVIDIA 高階顯示卡", brand: "NVIDIA", specs: "10240 CUDA / 16GB GDDR6X / 320W TDP / PCIe 4.0", image: "https://images.unsplash.com/photo-1727176763565-1d983341bb95", alt: "NVIDIA RTX 4080 Super graphics card side view", badge: "Hot", badgeColor: "bg-orange text-white", moq: "MOQ: 5 units", priceRange: [1000, 1200], stockStatus: "in-stock" },
{ id: "gpu-3", category: "gpu", name: "NVIDIA RTX 4070 Ti Super", nameZh: "NVIDIA 高效能顯示卡", brand: "NVIDIA", specs: "8448 CUDA / 16GB GDDR6X / 285W TDP / PCIe 4.0", image: "https://images.unsplash.com/photo-1727176763565-1d983341bb95", alt: "NVIDIA RTX 4070 Ti Super graphics card with dual fan", badge: "New", badgeColor: "bg-yellow-brand text-navy", moq: "MOQ: 5 units", priceRange: [780, 900], stockStatus: "in-stock" },
{ id: "gpu-4", category: "gpu", name: "NVIDIA RTX 4070 Super", nameZh: "NVIDIA 主流高階顯示卡", brand: "NVIDIA", specs: "7168 CUDA / 12GB GDDR6X / 220W TDP / PCIe 4.0", image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e77cf40b-1772174255365.png", alt: "NVIDIA RTX 4070 Super graphics card for gaming", badge: "Popular", badgeColor: "bg-navy text-white", moq: "MOQ: 10 units", priceRange: [580, 680], stockStatus: "in-stock" },
{ id: "gpu-5", category: "gpu", name: "NVIDIA RTX 4060 Ti", nameZh: "NVIDIA 主流顯示卡", brand: "NVIDIA", specs: "4352 CUDA / 16GB GDDR6 / 165W TDP / PCIe 4.0", image: "https://img.rocket.new/generatedImages/rocket_gen_img_12fc63759-1772174254036.png", alt: "NVIDIA RTX 4060 Ti graphics card for 1080p gaming", badge: "Best Seller", badgeColor: "bg-navy text-white", moq: "MOQ: 10 units", priceRange: [380, 450], stockStatus: "in-stock" },
{ id: "gpu-6", category: "gpu", name: "AMD RX 7900 XTX", nameZh: "AMD 旗艦顯示卡", brand: "AMD", specs: "6144 CUs / 24GB GDDR6 / 355W TDP / PCIe 4.0", image: "https://img.rocket.new/generatedImages/rocket_gen_img_19f73317f-1772079185855.png", alt: "AMD Radeon RX 7900 XTX flagship graphics card", badge: "AMD Top", badgeColor: "bg-red-600 text-white", moq: "MOQ: 5 units", priceRange: [900, 1100], stockStatus: "in-stock" },
{ id: "gpu-7", category: "gpu", name: "AMD RX 7800 XT", nameZh: "AMD 高效能顯示卡", brand: "AMD", specs: "3840 CUs / 16GB GDDR6 / 263W TDP / PCIe 4.0", image: "https://img.rocket.new/generatedImages/rocket_gen_img_11b936fa3-1772174258605.png", alt: "AMD Radeon RX 7800 XT graphics card for 1440p gaming", badge: "Value", badgeColor: "bg-green-600 text-white", moq: "MOQ: 10 units", priceRange: [450, 550], stockStatus: "in-stock" },
// ── RAM ──────────────────────────────────────────────────────────────
{ id: "ram-1", category: "ram", name: "G.Skill Trident Z5 RGB DDR5-6000 32GB", nameZh: "G.Skill DDR5 高速記憶體", brand: "G.Skill", specs: "32GB (2x16GB) / DDR5-6000 / CL30 / RGB", image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f7cf6c32-1771516325185.png", alt: "G.Skill Trident Z5 RGB DDR5 RAM sticks with colorful lighting", badge: "DDR5", badgeColor: "bg-orange text-white", moq: "MOQ: 20 units", priceRange: [120, 160], stockStatus: "in-stock" },
{ id: "ram-2", category: "ram", name: "Corsair Dominator Platinum DDR5-6200 64GB", nameZh: "Corsair DDR5 旗艦記憶體", brand: "Corsair", specs: "64GB (2x32GB) / DDR5-6200 / CL36 / RGB", image: "https://img.rocket.new/generatedImages/rocket_gen_img_1967f87ab-1765244545316.png", alt: "Corsair Dominator Platinum DDR5 RAM kit with premium heatspreader", badge: "Premium", badgeColor: "bg-navy text-white", moq: "MOQ: 10 units", priceRange: [280, 360], stockStatus: "in-stock" },
{ id: "ram-3", category: "ram", name: "Kingston Fury Beast DDR5-5200 32GB", nameZh: "Kingston DDR5 高效能記憶體", brand: "Kingston", specs: "32GB (2x16GB) / DDR5-5200 / CL40 / Low Profile", image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d663d269-1772174255241.png", alt: "Kingston Fury Beast DDR5 RAM modules in low profile design", badge: "Value", badgeColor: "bg-green-600 text-white", moq: "MOQ: 20 units", priceRange: [90, 120], stockStatus: "in-stock" },
{ id: "ram-4", category: "ram", name: "G.Skill Ripjaws S5 DDR5-6000 96GB", nameZh: "G.Skill DDR5 大容量記憶體", brand: "G.Skill", specs: "96GB (2x48GB) / DDR5-6000 / CL30 / XMP 3.0", image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fd3a00e8-1772174254614.png", alt: "G.Skill Ripjaws S5 DDR5 96GB high capacity RAM kit", badge: "96GB", badgeColor: "bg-orange text-white", moq: "MOQ: 10 units", priceRange: [380, 460], stockStatus: "low-stock" },
// ── Motherboard ──────────────────────────────────────────────────────
{ id: "mb-1", category: "motherboard", name: "ASUS ROG Maximus Z790 Hero", nameZh: "ASUS ROG 旗艦主機板", brand: "ASUS", specs: "Z790 / LGA1700 / DDR5 / PCIe 5.0 / WiFi 6E", image: "https://img.rocket.new/generatedImages/rocket_gen_img_1daa5fe44-1772174254010.png", alt: "ASUS ROG Maximus Z790 Hero flagship motherboard with RGB lighting", badge: "Flagship", badgeColor: "bg-orange text-white", moq: "MOQ: 5 units", priceRange: [600, 750], stockStatus: "in-stock" },
{ id: "mb-2", category: "motherboard", name: "MSI MEG Z790 ACE", nameZh: "MSI MEG 高階主機板", brand: "MSI", specs: "Z790 / LGA1700 / DDR5 / PCIe 5.0 / Thunderbolt 4", image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b7d14a3d-1772174256679.png", alt: "MSI MEG Z790 ACE high-end motherboard for Intel 13th 14th gen", badge: "Premium", badgeColor: "bg-navy text-white", moq: "MOQ: 5 units", priceRange: [500, 620], stockStatus: "in-stock" },
{ id: "mb-3", category: "motherboard", name: "Gigabyte X670E Aorus Master", nameZh: "技嘉 AMD AM5 旗艦主機板", brand: "Gigabyte", specs: "X670E / AM5 / DDR5 / PCIe 5.0 / WiFi 6E", image: "https://img.rocket.new/generatedImages/rocket_gen_img_129294719-1772174254614.png", alt: "Gigabyte X670E Aorus Master AMD AM5 motherboard", badge: "AM5", badgeColor: "bg-red-600 text-white", moq: "MOQ: 5 units", priceRange: [450, 560], stockStatus: "in-stock" },
{ id: "mb-4", category: "motherboard", name: "ASRock B650E Taichi", nameZh: "ASRock AMD B650E 主機板", brand: "ASRock", specs: "B650E / AM5 / DDR5 / PCIe 5.0 / 2.5G LAN", image: "https://img.rocket.new/generatedImages/rocket_gen_img_1668ef59e-1771516321403.png", alt: "ASRock B650E Taichi AMD motherboard with premium features", badge: "Value", badgeColor: "bg-green-600 text-white", moq: "MOQ: 10 units", priceRange: [280, 360], stockStatus: "in-stock" },
// ── Storage ──────────────────────────────────────────────────────────
{ id: "ssd-1", category: "storage", name: "Samsung 990 Pro 2TB NVMe", nameZh: "三星 PCIe 4.0 旗艦固態硬碟", brand: "Samsung", specs: "2TB / PCIe 4.0 NVMe / 7450MB/s Read / M.2 2280", image: "https://images.unsplash.com/photo-1698074089025-f3436a775d10", alt: "Samsung 990 Pro 2TB NVMe SSD M.2 form factor", badge: "7450MB/s", badgeColor: "bg-orange text-white", moq: "MOQ: 20 units", priceRange: [150, 200], stockStatus: "in-stock" },
{ id: "ssd-2", category: "storage", name: "WD Black SN850X 4TB", nameZh: "WD Black PCIe 4.0 固態硬碟", brand: "WD", specs: "4TB / PCIe 4.0 NVMe / 7300MB/s Read / M.2 2280", image: "https://img.rocket.new/generatedImages/rocket_gen_img_115bfd5cb-1765792102815.png", alt: "WD Black SN850X 4TB NVMe SSD for gaming and workstation", badge: "4TB", badgeColor: "bg-navy text-white", moq: "MOQ: 10 units", priceRange: [280, 360], stockStatus: "in-stock" },
{ id: "ssd-3", category: "storage", name: "Seagate FireCuda 530 2TB", nameZh: "Seagate PCIe 4.0 高速固態硬碟", brand: "Seagate", specs: "2TB / PCIe 4.0 NVMe / 7300MB/s Read / M.2 2280", image: "https://img.rocket.new/generatedImages/rocket_gen_img_1170704cc-1771553641273.png", alt: "Seagate FireCuda 530 2TB NVMe SSD with heatsink", badge: "Fast", badgeColor: "bg-orange text-white", moq: "MOQ: 20 units", priceRange: [140, 180], stockStatus: "in-stock" },
// ── Cooling ──────────────────────────────────────────────────────────
{ id: "cool-1", category: "cooling", name: "Noctua NH-D15 Chromax Black", nameZh: "Noctua 旗艦塔式散熱器", brand: "Noctua", specs: "Dual Tower / 2x140mm Fan / 250W TDP / LGA1700 AM5", image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fb2f8610-1772079188472.png", alt: "Noctua NH-D15 Chromax Black dual tower CPU cooler", badge: "Silent", badgeColor: "bg-navy text-white", moq: "MOQ: 10 units", priceRange: [100, 130], stockStatus: "in-stock" },
{ id: "cool-2", category: "cooling", name: "Corsair H150i Elite LCD 360mm", nameZh: "Corsair 360mm 一體式水冷", brand: "Corsair", specs: "360mm AIO / 3x120mm Fan / LCD Display / RGB", image: "https://img.rocket.new/generatedImages/rocket_gen_img_16770ea04-1772174256075.png", alt: "Corsair H150i Elite LCD 360mm AIO liquid cooler with display", badge: "AIO", badgeColor: "bg-orange text-white", moq: "MOQ: 10 units", priceRange: [180, 240], stockStatus: "in-stock" },
{ id: "cool-3", category: "cooling", name: "be quiet! Dark Rock Pro 5", nameZh: "be quiet! 旗艦靜音散熱器", brand: "be quiet!", specs: "Dual Tower / 2x135mm Fan / 250W TDP / Ultra Silent", image: "https://img.rocket.new/generatedImages/rocket_gen_img_16d486517-1772174254166.png", alt: "be quiet! Dark Rock Pro 5 dual tower silent CPU cooler", badge: "Ultra Silent", badgeColor: "bg-navy text-white", moq: "MOQ: 10 units", priceRange: [90, 120], stockStatus: "in-stock" },
// ── PSU ──────────────────────────────────────────────────────────────
{ id: "psu-1", category: "psu", name: "Seasonic Prime TX-1000", nameZh: "Seasonic 1000W 鈦金電源", brand: "Seasonic", specs: "1000W / 80+ Titanium / Fully Modular / 12VHPWR", image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c348f811-1772174255250.png", alt: "Seasonic Prime TX-1000 titanium rated fully modular power supply", badge: "Titanium", badgeColor: "bg-orange text-white", moq: "MOQ: 10 units", priceRange: [250, 320], stockStatus: "in-stock" },
{ id: "psu-2", category: "psu", name: "Corsair RM1000x Shift", nameZh: "Corsair 1000W 金牌電源", brand: "Corsair", specs: "1000W / 80+ Gold / Fully Modular / Side Connector", image: "https://img.rocket.new/generatedImages/rocket_gen_img_14165829c-1772174257537.png", alt: "Corsair RM1000x Shift 1000W gold rated modular PSU", badge: "Gold", badgeColor: "bg-yellow-brand text-navy", moq: "MOQ: 10 units", priceRange: [180, 230], stockStatus: "in-stock" },
{ id: "psu-3", category: "psu", name: "be quiet! Dark Power 13 850W", nameZh: "be quiet! 850W 鈦金電源", brand: "be quiet!", specs: "850W / 80+ Titanium / Fully Modular / Silent", image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d99473e7-1772174254810.png", alt: "be quiet! Dark Power 13 850W titanium PSU ultra silent", badge: "Silent", badgeColor: "bg-navy text-white", moq: "MOQ: 10 units", priceRange: [200, 260], stockStatus: "in-stock" },
// ── Case ──────────────────────────────────────────────────────────────
{ id: "case-1", category: "case", name: "Fractal Design Torrent", nameZh: "Fractal Design 高氣流機殼", brand: "Fractal Design", specs: "ATX / High Airflow / 2x180mm Front Fan / Tempered Glass", image: "https://img.rocket.new/generatedImages/rocket_gen_img_14448e8cd-1772174254048.png", alt: "Fractal Design Torrent ATX case with high airflow front panel", badge: "Airflow", badgeColor: "bg-lightblue text-white", moq: "MOQ: 5 units", priceRange: [150, 200], stockStatus: "in-stock" },
{ id: "case-2", category: "case", name: "Lian Li O11 Dynamic EVO", nameZh: "聯力 O11 Dynamic EVO 機殼", brand: "Lian Li", specs: "ATX / Dual Chamber / 360mm Radiator / Tempered Glass", image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c2bbdcae-1765445309346.png", alt: "Lian Li O11 Dynamic EVO dual chamber PC case", badge: "Popular", badgeColor: "bg-navy text-white", moq: "MOQ: 5 units", priceRange: [160, 210], stockStatus: "in-stock" },
{ id: "case-3", category: "case", name: "NZXT H9 Elite", nameZh: "NZXT H9 Elite 雙腔機殼", brand: "NZXT", specs: "ATX / Dual Chamber / 4-Sided Glass / 360mm Radiator", image: "https://img.rocket.new/generatedImages/rocket_gen_img_127ea622c-1772174255423.png", alt: "NZXT H9 Elite dual chamber case with four-sided tempered glass", badge: "Glass", badgeColor: "bg-lightblue text-white", moq: "MOQ: 5 units", priceRange: [200, 260], stockStatus: "in-stock" }];


const CATEGORIES = [
{ id: "all", label: "全部 All" },
{ id: "cpu", label: "CPU 處理器" },
{ id: "gpu", label: "GPU 顯示卡" },
{ id: "ram", label: "RAM 記憶體" },
{ id: "motherboard", label: "主機板" },
{ id: "storage", label: "Storage 儲存" },
{ id: "cooling", label: "Cooling 散熱" },
{ id: "psu", label: "PSU 電源" },
{ id: "case", label: "Case 機殼" }];


const STOCK_COLORS: Record<string, string> = {
  "in-stock": "bg-green-100 text-green-700",
  "low-stock": "bg-yellow-100 text-yellow-700",
  "out-of-stock": "bg-red-100 text-red-700"
};

const STOCK_LABELS: Record<string, string> = {
  "in-stock": "In Stock",
  "low-stock": "Low Stock",
  "out-of-stock": "Out of Stock"
};

export default function ComparisonPageContent() {
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [savedComparisons, setSavedComparisons] = useState<ComparisonRecord[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [savingComparison, setSavingComparison] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState<"select" | "compare" | "history">("select");

  useEffect(() => {
    loadHistory();
  }, []);

  async function loadHistory() {
    setLoadingHistory(true);
    try {
      const history = await comparisonService.getHistory();
      setSavedComparisons(history);
    } catch {
      setSavedComparisons([]);
    } finally {
      setLoadingHistory(false);
    }
  }

  async function handleSaveComparison() {
    if (selectedProducts.length < 2) return;
    setSavingComparison(true);
    try {
      await comparisonService.saveComparison(
        selectedProducts.map((p) => ({ id: p.id, name: p.name }))
      );
      setSaveSuccess(true);
      await loadHistory();
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch {

      // silent
    } finally {setSavingComparison(false);
    }
  }

  function toggleProduct(product: Product) {
    setSelectedProducts((prev) => {
      const exists = prev.find((p) => p.id === product.id);
      if (exists) return prev.filter((p) => p.id !== product.id);
      if (prev.length >= 4) return prev;
      return [...prev, product];
    });
  }

  function loadSavedComparison(record: ComparisonRecord) {
    const products = record.productIds.
    map((id) => ALL_PRODUCTS.find((p) => p.id === id)).
    filter(Boolean) as Product[];
    setSelectedProducts(products);
    setActiveTab("compare");
  }

  const filteredProducts = useMemo(() => {
    return ALL_PRODUCTS.filter((p) => {
      const matchCat = activeCategory === "all" || p.category === activeCategory;
      const q = searchQuery.toLowerCase();
      const matchSearch =
      !q ||
      p.name.toLowerCase().includes(q) ||
      p.nameZh.includes(q) ||
      p.brand.toLowerCase().includes(q) ||
      p.specs.toLowerCase().includes(q);
      return matchCat && matchSearch;
    });
  }, [activeCategory, searchQuery]);

  const parseSpecs = (specs: string) => specs.split("/").map((s) => s.trim());
  const maxSpecCount = selectedProducts.length > 0 ?
  Math.max(...selectedProducts.map((p) => parseSpecs(p.specs).length)) :
  0;

  return (
    <div className="min-h-screen bg-bg-light">
      {/* Hero */}
      <section className="bg-navy pt-28 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-orange rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-lightblue rounded-full blur-3xl" />
        </div>
        <div className="max-w-8xl mx-auto px-6 md:px-10 relative">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-orange/20 rounded-2xl flex items-center justify-center">
              <Icon name="ScaleIcon" size={24} className="text-orange" />
            </div>
            <span className="text-orange font-bold text-sm uppercase tracking-widest">Product Comparison</span>
          </div>
          <h1 className="font-heading font-black text-4xl md:text-5xl text-white mb-3">
            Compare Products
            <span className="block text-orange text-2xl md:text-3xl mt-1">產品規格比較</span>
          </h1>
          <p className="text-white/70 text-lg max-w-2xl">
            Select up to 4 products to compare specifications side-by-side. Save comparisons for future reference.
          </p>
          {/* Stats */}
          <div className="flex flex-wrap gap-6 mt-8">
            <div className="flex items-center gap-2 text-white/80">
              <Icon name="CubeIcon" size={18} className="text-orange" />
              <span className="text-sm font-semibold">{ALL_PRODUCTS.length} Products Available</span>
            </div>
            <div className="flex items-center gap-2 text-white/80">
              <Icon name="ArrowsRightLeftIcon" size={18} className="text-orange" />
              <span className="text-sm font-semibold">Compare up to 4 at once</span>
            </div>
            <div className="flex items-center gap-2 text-white/80">
              <Icon name="BookmarkIcon" size={18} className="text-orange" />
              <span className="text-sm font-semibold">Save & reload comparisons</span>
            </div>
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-border sticky top-20 z-30 shadow-sm">
        <div className="max-w-8xl mx-auto px-6 md:px-10">
          <div className="flex gap-0">
            {([
            { id: "select", label: "Select Products", labelZh: "選擇產品", icon: "PlusCircleIcon" },
            { id: "compare", label: "Compare", labelZh: `比較 (${selectedProducts.length})`, icon: "ArrowsRightLeftIcon" },
            { id: "history", label: "Saved", labelZh: `已儲存 (${savedComparisons.length})`, icon: "BookmarkIcon" }] as
            const).map((tab) =>
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-4 font-bold text-sm border-b-2 transition-all ${
              activeTab === tab.id ?
              "border-orange text-orange" : "border-transparent text-text-muted hover:text-navy"}`
              }>
              
                <Icon name={tab.icon} size={18} />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="text-xs text-text-muted">{tab.labelZh}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-8xl mx-auto px-6 md:px-10 py-10">

        {/* ── SELECT TAB ── */}
        {activeTab === "select" &&
        <div>
            {/* Selected indicator */}
            {selectedProducts.length > 0 &&
          <div className="mb-6 bg-orange/10 border border-orange/30 rounded-2xl px-6 py-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <Icon name="ScaleIcon" size={20} className="text-orange" />
                  <span className="font-bold text-navy text-sm">{selectedProducts.length}/4 products selected</span>
                  <div className="flex gap-2 flex-wrap">
                    {selectedProducts.map((p) =>
                <span key={p.id} className="flex items-center gap-1 bg-white border border-orange/30 rounded-full px-3 py-1 text-xs font-bold text-navy">
                        {p.name}
                        <button onClick={() => toggleProduct(p)} className="ml-1 text-text-muted hover:text-orange">
                          <Icon name="XMarkIcon" size={12} />
                        </button>
                      </span>
                )}
                  </div>
                </div>
                <button
              onClick={() => setActiveTab("compare")}
              disabled={selectedProducts.length < 2}
              className="flex items-center gap-2 bg-orange hover:bg-orange-dark disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-5 py-2.5 rounded-full font-black text-sm transition-all">
              
                  <Icon name="ArrowsRightLeftIcon" size={16} variant="solid" />
                  Compare Now
                </button>
              </div>
          }

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Icon name="MagnifyingGlassIcon" size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
                <input
                type="text"
                placeholder="Search products... 搜尋產品"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-11 pr-4 py-3 border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange/30 focus:border-orange bg-white" />
              
              </div>
              <div className="flex gap-2 flex-wrap">
                {CATEGORIES.map((cat) =>
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap ${
                activeCategory === cat.id ?
                "bg-navy text-white" : "bg-white border border-border text-text-mid hover:border-navy"}`
                }>
                
                    {cat.label}
                  </button>
              )}
              </div>
            </div>

            {/* Product Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filteredProducts.map((product) => {
              const isSelected = selectedProducts.some((p) => p.id === product.id);
              const isDisabled = !isSelected && selectedProducts.length >= 4;
              return (
                <div
                  key={product.id}
                  onClick={() => !isDisabled && toggleProduct(product)}
                  className={`relative bg-white rounded-2xl border-2 overflow-hidden transition-all cursor-pointer group ${
                  isSelected ?
                  "border-orange shadow-orange-glow" :
                  isDisabled ?
                  "border-border opacity-50 cursor-not-allowed" : "border-border hover:border-navy hover:shadow-navy-sm"}`
                  }>
                  
                    {/* Selection indicator */}
                    <div className={`absolute top-3 right-3 z-10 w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all ${
                  isSelected ? "bg-orange border-orange" : "bg-white border-border"}`
                  }>
                      {isSelected && <Icon name="CheckIcon" size={14} className="text-white" />}
                    </div>

                    {/* Image */}
                    <div className="relative h-44 bg-bg-light overflow-hidden">
                      <AppImage
                      src={product.image}
                      alt={product.alt}
                      fill
                      className="object-cover group-hover:scale-105 transition-transform duration-300"
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw" />
                    
                      <span className={`absolute top-3 left-3 text-xs font-black px-2.5 py-1 rounded-full ${product.badgeColor}`}>
                        {product.badge}
                      </span>
                    </div>

                    {/* Info */}
                    <div className="p-4">
                      <p className="text-xs font-bold text-orange uppercase tracking-wider mb-1">{product.brand}</p>
                      <h3 className="font-heading font-black text-navy text-sm leading-tight mb-1">{product.name}</h3>
                      <p className="text-xs text-text-muted mb-2">{product.nameZh}</p>
                      <p className="text-xs text-text-mid font-mono leading-relaxed mb-3">{product.specs}</p>
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${STOCK_COLORS[product.stockStatus]}`}>
                          {STOCK_LABELS[product.stockStatus]}
                        </span>
                        <span className="text-xs text-text-muted">{product.moq}</span>
                      </div>
                    </div>
                  </div>);

            })}
            </div>

            {filteredProducts.length === 0 &&
          <div className="text-center py-20">
                <Icon name="MagnifyingGlassIcon" size={48} className="text-text-muted mx-auto mb-4" />
                <p className="text-text-muted font-semibold">No products found. Try adjusting your search or filters.</p>
              </div>
          }
          </div>
        }

        {/* ── COMPARE TAB ── */}
        {activeTab === "compare" &&
        <div>
            {selectedProducts.length < 2 ?
          <div className="text-center py-24">
                <div className="w-20 h-20 bg-navy/10 rounded-3xl flex items-center justify-center mx-auto mb-6">
                  <Icon name="ScaleIcon" size={40} className="text-navy/40" />
                </div>
                <h3 className="font-heading font-black text-2xl text-navy mb-2">No Products Selected</h3>
                <p className="text-text-muted mb-6">Select at least 2 products from the product list to compare.</p>
                <button
              onClick={() => setActiveTab("select")}
              className="inline-flex items-center gap-2 bg-orange text-white px-6 py-3 rounded-full font-bold text-sm">
              
                  <Icon name="PlusCircleIcon" size={18} />
                  Select Products
                </button>
              </div> :

          <div>
                {/* Action bar */}
                <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <Icon name="ArrowsRightLeftIcon" size={22} className="text-orange" />
                    <h2 className="font-heading font-black text-xl text-navy">
                      Comparing {selectedProducts.length} Products
                    </h2>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                  onClick={() => setActiveTab("select")}
                  className="flex items-center gap-2 border border-border text-text-mid hover:border-navy hover:text-navy px-4 py-2.5 rounded-xl font-bold text-sm transition-all">
                  
                      <Icon name="PlusCircleIcon" size={16} />
                      Edit Selection
                    </button>
                    <button
                  onClick={handleSaveComparison}
                  disabled={savingComparison}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm transition-all ${
                  saveSuccess ?
                  "bg-green-500 text-white" : "bg-navy hover:bg-navy-dark text-white"}`
                  }>
                  
                      <Icon name={saveSuccess ? "CheckIcon" : "BookmarkIcon"} size={16} />
                      {savingComparison ? "Saving..." : saveSuccess ? "Saved!" : "Save Comparison"}
                    </button>
                    <button
                  onClick={() => setSelectedProducts([])}
                  className="flex items-center gap-2 border border-red-200 text-red-500 hover:bg-red-50 px-4 py-2.5 rounded-xl font-bold text-sm transition-all">
                  
                      <Icon name="TrashIcon" size={16} />
                      Clear
                    </button>
                  </div>
                </div>

                {/* Comparison Table */}
                <div className="bg-white rounded-3xl border border-border overflow-hidden shadow-navy-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-navy">
                          <th className="text-left px-6 py-5 font-heading font-bold text-white/70 text-xs uppercase tracking-wider w-44">
                            Specification
                          </th>
                          {selectedProducts.map((product) =>
                      <th key={product.id} className="px-6 py-5 min-w-[260px]">
                              <div className="flex flex-col items-center gap-3">
                                <div className="relative w-28 h-28 rounded-2xl overflow-hidden border-2 border-white/20">
                                  <AppImage
                              src={product.image}
                              alt={product.alt}
                              fill
                              className="object-cover"
                              sizes="112px" />
                            
                                </div>
                                <div className="text-center">
                                  <span className={`text-xs font-black px-2.5 py-1 rounded-full ${product.badgeColor} inline-block mb-1`}>
                                    {product.badge}
                                  </span>
                                  <h3 className="font-heading font-black text-white text-sm leading-tight mb-0.5">
                                    {product.name}
                                  </h3>
                                  <p className="text-xs text-white/60 mb-0.5">{product.nameZh}</p>
                                  <p className="text-xs font-bold text-orange uppercase">{product.brand}</p>
                                </div>
                                <button
                            onClick={() => toggleProduct(product)}
                            className="text-white/40 hover:text-red-400 transition-colors"
                            aria-label="Remove from comparison">
                            
                                  <Icon name="XMarkIcon" size={18} />
                                </button>
                              </div>
                            </th>
                      )}
                        </tr>
                      </thead>
                      <tbody>
                        {/* Category */}
                        <tr className="border-t border-border">
                          <td className="px-6 py-4 font-bold text-navy text-xs uppercase tracking-wider bg-navy-50">
                            <div className="flex items-center gap-2">
                              <Icon name="TagIcon" size={14} />
                              Category
                            </div>
                          </td>
                          {selectedProducts.map((p) =>
                      <td key={p.id} className="px-6 py-4 text-center text-sm font-bold text-navy">
                              {p.category.toUpperCase()}
                            </td>
                      )}
                        </tr>

                        {/* Specs rows */}
                        {Array.from({ length: maxSpecCount }).map((_, i) =>
                    <tr key={i} className={`border-t border-border ${i % 2 === 0 ? "bg-white" : "bg-bg-light/50"}`}>
                            <td className="px-6 py-4 font-bold text-navy text-xs uppercase tracking-wider bg-navy-50">
                              <div className="flex items-center gap-2">
                                <Icon name="CpuChipIcon" size={14} />
                                Spec {i + 1}
                              </div>
                            </td>
                            {selectedProducts.map((p) => {
                        const specs = parseSpecs(p.specs);
                        return (
                          <td key={p.id} className="px-6 py-4 text-center text-sm font-mono text-text-mid">
                                  {specs[i] || <span className="text-text-muted">—</span>}
                                </td>);

                      })}
                          </tr>
                    )}

                        {/* Stock */}
                        <tr className="border-t border-border">
                          <td className="px-6 py-4 font-bold text-navy text-xs uppercase tracking-wider bg-navy-50">
                            <div className="flex items-center gap-2">
                              <Icon name="ArchiveBoxIcon" size={14} />
                              Stock
                            </div>
                          </td>
                          {selectedProducts.map((p) =>
                      <td key={p.id} className="px-6 py-4 text-center">
                              <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${STOCK_COLORS[p.stockStatus]}`}>
                                {STOCK_LABELS[p.stockStatus]}
                              </span>
                            </td>
                      )}
                        </tr>

                        {/* MOQ */}
                        <tr className="border-t border-border">
                          <td className="px-6 py-4 font-bold text-navy text-xs uppercase tracking-wider bg-navy-50">
                            <div className="flex items-center gap-2">
                              <Icon name="CubeIcon" size={14} />
                              Min. Order
                            </div>
                          </td>
                          {selectedProducts.map((p) =>
                      <td key={p.id} className="px-6 py-4 text-center text-sm font-bold text-navy">
                              {p.moq}
                            </td>
                      )}
                        </tr>

                        {/* Price Range */}
                        <tr className="border-t border-border">
                          <td className="px-6 py-4 font-bold text-navy text-xs uppercase tracking-wider bg-navy-50">
                            <div className="flex items-center gap-2">
                              <Icon name="CurrencyDollarIcon" size={14} />
                              Price Range
                            </div>
                          </td>
                          {selectedProducts.map((p) =>
                      <td key={p.id} className="px-6 py-4 text-center text-sm font-bold text-orange">
                              ${p.priceRange[0]} – ${p.priceRange[1]}
                            </td>
                      )}
                        </tr>

                        {/* Inquiry */}
                        <tr className="border-t border-border bg-bg-light">
                          <td className="px-6 py-4 font-bold text-navy text-xs uppercase tracking-wider bg-navy-50">
                            <div className="flex items-center gap-2">
                              <Icon name="ChatBubbleLeftRightIcon" size={14} />
                              Inquiry
                            </div>
                          </td>
                          {selectedProducts.map((p) =>
                      <td key={p.id} className="px-6 py-4 text-center">
                              <a
                          href="https://wa.me/85259773386"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all">
                          
                                <Icon name="ChatBubbleLeftRightIcon" size={12} variant="solid" />
                                Get Quote
                              </a>
                            </td>
                      )}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
          }
          </div>
        }

        {/* ── HISTORY TAB ── */}
        {activeTab === "history" &&
        <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-heading font-black text-2xl text-navy">Saved Comparisons</h2>
                <p className="text-text-muted text-sm mt-1">已儲存的比較記錄 · Your session comparison history</p>
              </div>
              <button
              onClick={loadHistory}
              className="flex items-center gap-2 border border-border text-text-mid hover:border-navy hover:text-navy px-4 py-2.5 rounded-xl font-bold text-sm transition-all">
              
                <Icon name="ArrowPathIcon" size={16} />
                Refresh
              </button>
            </div>

            {loadingHistory ?
          <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3 text-text-muted">
                  <div className="w-6 h-6 border-2 border-orange border-t-transparent rounded-full animate-spin" />
                  <span className="font-semibold">Loading history...</span>
                </div>
              </div> :
          savedComparisons.length === 0 ?
          <div className="text-center py-24">
                <div className="w-20 h-20 bg-navy/10 rounded-3xl flex items-center justify-center mx-auto mb-6">
                  <Icon name="BookmarkIcon" size={40} className="text-navy/40" />
                </div>
                <h3 className="font-heading font-black text-2xl text-navy mb-2">No Saved Comparisons</h3>
                <p className="text-text-muted mb-6">Compare products and save them to see your history here.</p>
                <button
              onClick={() => setActiveTab("select")}
              className="inline-flex items-center gap-2 bg-orange text-white px-6 py-3 rounded-full font-bold text-sm">
              
                  <Icon name="PlusCircleIcon" size={18} />
                  Start Comparing
                </button>
              </div> :

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {savedComparisons.map((record, idx) =>
            <div key={idx} className="bg-white rounded-2xl border border-border p-5 hover:border-navy hover:shadow-navy-sm transition-all">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <div className="w-9 h-9 bg-orange/10 rounded-xl flex items-center justify-center">
                          <Icon name="ScaleIcon" size={18} className="text-orange" />
                        </div>
                        <div>
                          <p className="font-bold text-navy text-sm">{record.productNames.length} Products</p>
                          <p className="text-xs text-text-muted">Saved comparison</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {record.productNames.map((name, i) =>
                <span key={i} className="text-xs bg-navy-50 text-navy font-semibold px-2.5 py-1 rounded-lg">
                          {name}
                        </span>
                )}
                    </div>
                    <button
                onClick={() => loadSavedComparison(record)}
                className="w-full flex items-center justify-center gap-2 bg-navy hover:bg-navy-dark text-white py-2.5 rounded-xl font-bold text-sm transition-all">
                
                      <Icon name="ArrowsRightLeftIcon" size={16} />
                      Load & Compare
                    </button>
                  </div>
            )}
              </div>
          }
          </div>
        }
      </div>
    </div>);

}